// 마스터 유저관리 (/ma/ma15) — 화주/유저/탭/액션 타입 정의

export const USER_MANAGEMENT_TABS = ['active', 'pending', 'invitation-expired', 'activation-request'] as const

export type UserManagementTab = (typeof USER_MANAGEMENT_TABS)[number]

export const USER_MANAGEMENT_TAB_LABELS: Record<UserManagementTab, string> = {
  active: '가입완료',
  pending: '가입승인요청',
  'invitation-expired': '초대만료',
  'activation-request': '활성화요청',
}

export type OktaStatus = 'ACTIVE' | 'PROVISIONED' | 'STAGED' | 'DEPROVISIONED'

/** 상단 고정 그리드 — 화주(업체) 마스터 행 (컬럼 구성은 디자이너 시안 기준 — BE 협의 필요) */
export interface ShipperMasterRow {
  shipperCode: string
  shipperName: string
  isActive: boolean
  businessNumber: string
  ceoName: string
  companyName: string
  zipCode: string
  address: string
  multiShipperGroup: string[]
  phone: string
  deptNames: string[]
  createdBy: string
  createdAt: string
  updatedBy: string
  updatedAt: string
}

// LOGISPRD-8899 (PR #2127) MasterUserRow 미러. 아래 '주석: BE 미보유' 필드는 PR 2127 응답에 없어 추가 요청 대상.
export interface MasterUser {
  id: number
  email: string
  nickname: string
  shipperCode: string
  departmentName: string
  phone: Nullable<string>
  authCode: string
  isValid: boolean // 가입 승인 여부 (false=승인 대기)
  isActive: boolean // 정지/비활성 (false=정지)
  joinDate: string // 가입일자
  lastLogin: Nullable<string>
  inactiveOver90Days: boolean
  departmentManager: Nullable<string> // 책임자명 (BE 현재 null — 부서 엔티티 연동 후)
  multiOwner: Nullable<boolean> // 멀티화주여부 (BE 현재 null)
  oktaUserId: Nullable<string>
  oktaStatus: Nullable<OktaStatus>

  // ── BE 미보유 (PR #2127 응답에 없음) — UI 표시용, 추가 요청 대상 ──
  shipperName?: string // 화주명 (BE 는 shipperCode 만)
  userId?: string // 사용자 아이디
  authName?: string // 권한 라벨 (BE 는 authCode 코드만)
  originalShipperName?: string // 원화주명
  lastInvitedAt?: Nullable<string> // 초대만료 탭
  invitationExpired?: boolean
  activationRequestedAt?: Nullable<string> // 활성화요청 탭
}

export interface GetMasterShippersReq {
  shipperCodes?: string[] // 화주코드 다중 필터 (화주명은 유니크하지 않아 코드로 조회)
  email?: string // Okta 유저 ID(=email) — 해당 유저가 속한 화주 조회 (LOGISPRD-9131)
  page: number
  size?: number
}

export type GetMasterShippersRes = ApiPaginationV2<ShipperMasterRow>

/** LOGISPRD-8899 목록 조회 spec — tab / shipperCode / deptName / keyword / page / size */
export interface GetMasterUsersReq {
  tab: UserManagementTab
  shipperCode?: string
  deptName?: string
  email?: string
  keyword?: string
  page: number
  size?: number
}

export type GetMasterUsersRes = ApiPaginationV2<MasterUser>

/** 탭별 건수 — 탭 라벨 옆 카운트 표시용 (BE 협의 필요 — 목록 spec 외 신규) */
export interface GetMasterUserTabCountsReq {
  shipperCode?: string
  deptName?: string
  email?: string
}

export type MasterUserTabCounts = Record<UserManagementTab, number>

export type GetMasterUserTabCountsRes = ApiResponseV2<MasterUserTabCounts>

export interface ApproveUserReq {
  authCode: string
}

export interface RejectUserReq {
  reason?: string
}

export interface UpdateAuthCodeReq {
  authCode: string
}

export interface UpdateShipperGroupReq {
  shipperCode: string
  shipperName: string
  deptName: string
  multiShipperGroup: string[]
}

/** 유저 프로필 인라인 수정 — 변경 필드만 전달 (PUT /api/v1/master/users/{id}/profile) */
export interface UpdateUserProfileReq {
  nickname?: string
  phone?: string
  authCode?: string
  multiOwner?: boolean
}

/** 멀티화주그룹 저장 — PUT /api/v1/master/shippers/{shipperCode}/multi-group */
export interface UpdateShipperMultiGroupReq {
  shipperCodes: string[]
}

export interface CheckPhoneDuplicateReq {
  phone: string
  excludeUserId?: number
}

export type CheckPhoneDuplicateRes = ApiResponseV2<{ duplicated: boolean }>

export type UserActionRes = ApiResponseV2<null>

/** 벌크 액션(activate/deactivate/resend-invite/reset-password/reset-mfa) 부분성공 응답 */
export interface BulkActionResult {
  success: { id: number; active?: boolean }[]
  fail: { id: number; errorCode: string | null; message: string | null }[]
}
export type BulkActionRes = ApiResponseV2<BulkActionResult>

/** soft delete(LOGISPRD-9315) 응답 — success 는 삭제된 id 배열 */
export type DeleteActionRes = ApiResponseV2<{ success: number[]; fail: BulkActionResult['fail'] }>
